import { useState, useEffect } from "react";
import "../Style/Login.css";
import { LoginForm } from "../IF/interfaceType";
import { UserRole, useSessionStorage } from "../IF/userSession";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { API_BASE_URL } from "Utils/apiUtil";
import { Lock } from "@mui/icons-material";
import { VscEye, VscEyeClosed } from "react-icons/vsc";
import { createTitle } from "Utils/format";

const Login = () => {
  const initData: LoginForm = {
    companyCode: "",
    id: "",
    password: "",
  };

  // セッションのuserDataを取得関数
  const { deleteUserData, setUserData } = useSessionStorage();
  const [loginForm, setLoginForm] = useState<LoginForm>(initData);
  const navigation = useNavigate();
  // エラーメッセージ
  const [errorMessage, setErrorMessage] = useState<string>("");

  // フォーム入力のハンドラー
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { name, value } = e.target;

    // companyCode の場合は大文字変換
    if (name === "companyCode") {
      value = value.toUpperCase();
    }

    setLoginForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    document.title = createTitle("ログイン");
  }, []);

  // ユーザデータの初期化
  useEffect(() => {
    deleteUserData();
  }, [deleteUserData]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      // API呼び出し
      const response = await axios.post(
        `${API_BASE_URL}/Login/GetUser`,
        loginForm
      );

      if (response.data.id !== "" && response.data.id !== undefined) {
        const user = {
          ...response.data,
          displayCompanyCode: response.data.companyCode,
          displayCompanyName: response.data.companyName,
          companyCode:
            response.data.userRole === UserRole.YKC
              ? ""
              : response.data.companyCode,
          companyName:
            response.data.userRole === UserRole.YKC
              ? ""
              : response.data.companyName,
        };
        setUserData(user);
        if (response.data.userRole === UserRole.YKC) {
          navigation("/IdIssuance");
        } else {
          navigation("/Dashboard");
        }
      } else {
        setErrorMessage("登録されているユーザー情報がありません。");
        return;
      }
    } catch (error) {
      setErrorMessage("ユーザー情報の取得に失敗しました。");
      console.error("ユーザー情報の取得に失敗しました:", error);
      return;
    }
  };

  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          padding: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          backgroundColor: "background.default",
          borderRadius: 2,
          boxShadow: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Lock sx={{ mr: 1, color: "primary.main", fontSize: 40 }} />
          {/* アイコン */}
          <Typography
            component="h1"
            variant="h4"
            sx={{
              fontFamily: "Pacifico, cursive",
              background: "linear-gradient(45deg, #FF6F61 30%, #D84315 90%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textShadow: "2px 2px 4px rgba(0, 0, 0, 0.3)",
              whiteSpace: "nowrap",
            }}
          >
            ハーネスアラート
          </Typography>
        </Box>

        <Box sx={{ minHeight: "50px", width: "100%", mb: 0.5 }}>
          {errorMessage && (
            <Alert
              severity="error"
              sx={{
                width: "100%",
                borderRadius: 1,
                backgroundColor: "background.default",
                color: "#D8000C",
              }}
            >
              {errorMessage}
            </Alert>
          )}
        </Box>
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            required
            fullWidth
            id="companyCode"
            label="企業コード"
            name="companyCode"
            value={loginForm.companyCode}
            onChange={handleChange}
            margin="normal"
            variant="outlined"
          />
          <TextField
            required
            fullWidth
            id="id"
            label="ログインID"
            name="id"
            value={loginForm.id}
            onChange={handleChange}
            margin="normal"
            variant="outlined"
          />
          <TextField
            required
            fullWidth
            name="password"
            label="パスワード"
            type={showPassword ? "text" : "password"}
            id="password"
            value={loginForm.password}
            onChange={handleChange}
            margin="normal"
            variant="outlined"
            // 右端に目アイコンを表示する
            InputProps={{
              endAdornment:
                (loginForm.password ?? "").length > 0 ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        showPassword ? "パスワードを非表示" : "パスワードを表示"
                      }
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      sx={{ mr: 0.5 }}
                      edge="end"
                    >
                      {showPassword ? <VscEyeClosed /> : <VscEye />}
                    </IconButton>
                  </InputAdornment>
                ) : null,
            }}
            sx={{
              // Edge / IE 用
              "& input::-ms-reveal": {
                display: "none",
              },
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 3, mb: 2 }}
          >
            ログイン
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default Login;
